import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { useSite } from '../../lib/site';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, CheckboxField, ConfirmButton, EmptyState, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ConsentPanel } from './Consent';
import { JOINED, MEMBERSHIPS, MembershipArray, PAST, PENDING_LINKS, PendingLinkArray, SrAccept, SrMembership, SrMembershipUpdate, SrResume, keys, statusTone } from './api';

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: keys.memberships });
}

function Heading({ m }: { m: SrMembership }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900">{m.buildingName}</h3>
        <p className="text-sm text-slate-600">{m.buildingAddress}</p>
        <p className="text-sm text-slate-600">{t('srt.invite.from', { operator: m.operatorName })}</p>
      </div>
      <Badge tone={statusTone(m.status)}>{t(`srt.status.${m.status}`)}</Badge>
    </div>
  );
}

/** An invitation: read the statement, choose the site and name, accept or decline. */
function InvitationCard({ m, canManage }: { m: SrMembership; canManage: boolean }) {
  const caps = useCaps();
  const { sites } = useSite();
  const refresh = useRefresh();
  const toast = useToast();
  const eligible = sites.filter((s) => !s.isRemote && s.status === 'active' && s.timeZone === m.buildingTimeZone);
  const [siteId, setSiteId] = useState('');
  const [displayName, setDisplayName] = useState(caps.tenant?.name.slice(0, 60) ?? '');
  const [agreed, setAgreed] = useState(false);

  const accept = useMutation({
    mutationFn: () => {
      const body = SrAccept.parse({ siteId, displayName, consentVersion: m.currentConsentVersion });
      return api.post(`${MEMBERSHIPS}/${m.id}/accept`, { body, schema: SrMembership });
    },
    onSuccess: async () => {
      toast(t('srt.accept.done', { building: m.buildingName }));
      await refresh();
    },
  });
  const decline = useMutation({
    mutationFn: () => api.post(`${MEMBERSHIPS}/${m.id}/decline`, { schema: SrMembership }),
    onSuccess: async () => {
      toast(t('srt.decline.done'));
      await refresh();
    },
  });
  const ready = siteId !== '' && displayName.trim() !== '' && agreed && !accept.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) accept.mutate();
  };

  return (
    <Card>
      <div className="space-y-4">
        <Heading m={m} />
        <p className="text-xs text-slate-600">{t('srt.invite.invitedAt', { date: formatDateTime(m.invitedAt) })}</p>
        <ConsentPanel version={m.currentConsentVersion} />
        {canManage ? (
          <form className="space-y-4" onSubmit={submit}>
            {eligible.length === 0 ? (
              <p className="text-sm text-amber-900">{t('srt.accept.noSites')}</p>
            ) : (
              <SelectField label={t('srt.accept.site')} hint={t('srt.accept.siteHint', { zone: m.buildingTimeZone })} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">{t('srt.accept.siteChoose')}</option>
                {eligible.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </SelectField>
            )}
            <TextField
              label={t('srt.accept.displayName')}
              hint={t('srt.accept.displayNameHint')}
              maxLength={60}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <CheckboxField label={t('sr.consent.accept')} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <ErrorLine error={accept.error ?? decline.error} />
            <div className="flex flex-wrap gap-3">
              <Button type="submit" variant="primary" disabled={!ready}>
                {t('srt.accept.button')}
              </Button>
              <ConfirmButton label={t('srt.decline.button')} confirmLabel={t('srt.decline.confirm')} pending={decline.isPending} onConfirm={() => decline.mutate()} />
            </div>
          </form>
        ) : null}
      </div>
    </Card>
  );
}

function RenameForm({ m }: { m: SrMembership }) {
  const refresh = useRefresh();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(m.displayName ?? '');
  const rename = useMutation({
    mutationFn: () => api.patch(`${MEMBERSHIPS}/${m.id}`, { body: SrMembershipUpdate.parse({ displayName: name }), schema: SrMembership }),
    onSuccess: async () => {
      setEditing(false);
      toast(t('srt.member.rename.done'));
      await refresh();
    },
  });
  if (!editing) {
    return (
      <Button
        onClick={() => {
          setName(m.displayName ?? '');
          setEditing(true);
        }}
      >
        {t('srt.member.rename')}
      </Button>
    );
  }
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() !== '') rename.mutate();
      }}
    >
      <input
        aria-label={t('srt.member.rename.label', { building: m.buildingName })}
        className="min-h-10 w-56 rounded-md border border-slate-300 px-2 text-sm"
        maxLength={60}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button type="submit" variant="primary" disabled={rename.isPending || name.trim() === ''}>
        {t('common.save')}
      </Button>
      <Button onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
      <ErrorLine error={rename.error} />
    </form>
  );
}

/** A building the company has joined: rename, pause, resume, leave. */
function MembershipCard({ m, canManage }: { m: SrMembership; canManage: boolean }) {
  const refresh = useRefresh();
  const toast = useToast();
  const outdated = m.consentVersion !== m.currentConsentVersion;
  // The platform raised the sharing statement version: an active company is off the kiosk until it reads and accepts the new one.
  const needsReaccept = m.status === 'active' && (m.needsReaccept ?? outdated);
  const [agreed, setAgreed] = useState(false);
  const act = useMutation({
    mutationFn: ({ action }: { action: 'pause' | 'resume' | 'reaccept' | 'leave' }) =>
      action === 'resume' || action === 'reaccept'
        ? api.post(`${MEMBERSHIPS}/${m.id}/${action}`, { body: SrResume.parse({ consentVersion: m.currentConsentVersion }), schema: SrMembership })
        : api.post(`${MEMBERSHIPS}/${m.id}/${action}`, { schema: SrMembership }),
    onSuccess: async (_r, v) => {
      toast(v.action === 'leave' ? t('srt.leave.done', { building: m.buildingName }) : t(`srt.${v.action}.done`));
      await refresh();
    },
  });

  return (
    <Card>
      <div className="space-y-4">
        <Heading m={m} />
        <dl className="space-y-1 text-sm text-slate-800">
          <div>{t('srt.member.site', { site: m.siteName ?? '' })}</div>
          <div>{t('srt.member.displayName', { name: m.displayName ?? '' })}</div>
          {m.acceptedAt ? <div className="text-slate-600">{t('srt.member.since', { date: formatDateTime(m.acceptedAt) })}</div> : null}
        </dl>
        {m.status === 'leaving' ? (
          <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            {m.leavingUntil ? t('srt.leaving.until', { time: formatDateTime(m.leavingUntil) }) : t('srt.leaving.noTime')}
          </p>
        ) : null}
        {needsReaccept ? (
          <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            {t('srt.reaccept.banner')}
          </p>
        ) : null}
        {(m.status === 'paused' && outdated) || needsReaccept ? <ConsentPanel version={m.currentConsentVersion} /> : null}
        {needsReaccept && canManage ? <CheckboxField label={t('sr.consent.accept')} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} /> : null}
        {m.status === 'paused' && outdated && canManage ? (
          <CheckboxField label={t('sr.consent.accept')} hint={t('srt.resume.outdated')} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        ) : null}
        {canManage && m.status !== 'leaving' ? (
          <div className="space-y-3">
            <ErrorLine error={act.error} />
            <div className="flex flex-wrap items-center gap-3">
              {needsReaccept ? (
                <Button variant="primary" onClick={() => act.mutate({ action: 'reaccept' })} disabled={act.isPending || !agreed}>
                  {t('srt.reaccept.button')}
                </Button>
              ) : null}
              {m.status === 'active' ? <RenameForm m={m} /> : null}
              {m.status === 'active' ? <Button onClick={() => act.mutate({ action: 'pause' })} disabled={act.isPending}>{t('srt.pause.button')}</Button> : null}
              {m.status === 'paused' ? (
                <Button variant="primary" onClick={() => act.mutate({ action: 'resume' })} disabled={act.isPending || (outdated && !agreed)}>
                  {t('srt.resume.button')}
                </Button>
              ) : null}
              <ConfirmButton label={t('srt.leave.button')} confirmLabel={t('srt.leave.confirm')} pending={act.isPending} onConfirm={() => act.mutate({ action: 'leave' })} />
            </div>
            <p className="text-xs text-slate-600">{t('srt.pause.explain')}</p>
            <p className="text-xs text-slate-600">{t('srt.leave.explain')}</p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * New shared tablets waiting for this company's approval (SECSR1). A tablet in a shared building that asks to sign visitors in for the company
 * gets nothing until someone who manages shared reception approves it here. Anyone who can view shared reception sees the list.
 */
function PendingTablets({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.pending, queryFn: () => api.get(PENDING_LINKS, { schema: PendingLinkArray }) });
  const decide = useMutation({
    mutationFn: ({ id, verb }: { id: string; verb: 'approve' | 'deny' }) => api.post(`${PENDING_LINKS}/${id}/${verb}`, {}),
    onSuccess: async (_r, v) => {
      toast(t(v.verb === 'approve' ? 'srt.pending.approved' : 'srt.pending.denied'));
      await qc.invalidateQueries({ queryKey: keys.pending });
      await qc.invalidateQueries({ queryKey: ['kiosk-devices'] });
    },
  });
  const pending = q.data ?? [];
  if (pending.length === 0) return null;
  return (
    <Section id="sr-pending" title={t('srt.pending.title')} description={t('srt.pending.description')}>
      {pending.map((p) => (
        <Card key={p.linkDeviceId}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{t('srt.pending.item', { device: p.deviceName, building: p.buildingName })}</p>
              <p className="text-xs text-slate-600">{t('srt.pending.requested', { time: formatDateTime(p.requestedAt) })}</p>
            </div>
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" onClick={() => decide.mutate({ id: p.linkDeviceId, verb: 'approve' })} disabled={decide.isPending}>
                  {t('srt.pending.approve')}
                </Button>
                <ConfirmButton
                  label={t('srt.pending.deny')}
                  confirmLabel={t('srt.pending.deny.confirm')}
                  pending={decide.isPending}
                  onConfirm={() => decide.mutate({ id: p.linkDeviceId, verb: 'deny' })}
                />
              </div>
            ) : null}
          </div>
        </Card>
      ))}
      {!canManage ? <p className="text-xs text-slate-600">{t('srt.pending.viewOnly')}</p> : null}
      <ErrorLine error={decide.error} />
    </Section>
  );
}

function Section({ id, title, description, children }: { id: string; title: string; description: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="mb-8">
      <h2 id={id} className="text-lg font-semibold text-slate-900">
        {title}
      </h2>
      <p className="mb-3 text-sm text-slate-600">{description}</p>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

/** Tenant page: invitations to review, joined buildings, earlier ones. Registered at /manage/shared-reception. */
export function SharedReceptionPage() {
  const canManage = useCan('shared_reception.manage');
  const q = useQuery({ queryKey: keys.memberships, queryFn: () => api.get(MEMBERSHIPS, { schema: MembershipArray }) });
  const all = q.data ?? [];
  const invited = all.filter((m) => m.status === 'invited');
  const joined = all.filter((m) => JOINED.includes(m.status));
  const past = all.filter((m) => PAST.includes(m.status));

  return (
    <>
      <PageHeader title={t('srt.page.title')} description={t('srt.page.description')} />
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.isPending ? <Spinner /> : null}
      {q.data && all.length === 0 ? <EmptyState message={t('srt.empty')} /> : null}
      <PendingTablets canManage={canManage} />
      {invited.length > 0 ? (
        <Section id="sr-invited" title={t('srt.invitations.title')} description={t('srt.invitations.description')}>
          {invited.map((m) => (
            <InvitationCard key={m.id} m={m} canManage={canManage} />
          ))}
        </Section>
      ) : null}
      {joined.length > 0 ? (
        <Section id="sr-joined" title={t('srt.joined.title')} description={t('srt.joined.description')}>
          {joined.map((m) => (
            <MembershipCard key={m.id} m={m} canManage={canManage} />
          ))}
        </Section>
      ) : null}
      {past.length > 0 ? (
        <Section id="sr-past" title={t('srt.past.title')} description={t('srt.past.description')}>
          {past.map((m) => (
            <Card key={m.id}>
              <Heading m={m} />
            </Card>
          ))}
        </Section>
      ) : null}
    </>
  );
}
